import { useCallback, useEffect, useRef, useState } from 'react';
import type { ActionView } from './action-confirmation';
import { actionRequest } from './action-api';
import { actionTurnRunning } from './action-presentation';

type Grant = { key: string; token: string; expiresAt: number };
const keyFor = (action: ActionView | null, identity: string) =>
  action?.status === 'pending' &&
  action.canDecide &&
  !action.editing &&
  Date.parse(action.expiresAt) > Date.now()
    ? `${identity}:${action.id}:${action.digest}`
    : '';

/** Each mounted presentation owns an ephemeral grant, including compact + canvas. */
export function useActionCardGrant(action: ActionView | null, identity: string) {
  const [grant, setGrant] = useState<Grant | null>(null);
  const current = useRef({ action, identity, key: '' });
  const generation = useRef(0);
  const presented = useRef('');
  const requesting = useRef('');
  current.current = { action, identity, key: keyFor(action, identity) };
  const reset = useCallback(() => {
    generation.current += 1;
    presented.current = '';
    requesting.current = '';
    setGrant(null);
  }, []);
  const acknowledge = useCallback((shown: ActionView, force = false) => {
    const key = keyFor(shown, current.current.identity);
    if (!key || key !== current.current.key || actionTurnRunning(shown.threadId)) return;
    if (requesting.current === key || (!force && presented.current === key)) return;
    const version = ++generation.current;
    requesting.current = key;
    const active = () => version === generation.current && key === current.current.key;
    void actionRequest<{ presented: boolean; cardToken: string; cardTokenExpiresAt: string }>(
      shown.id,
      '/presented',
      'POST',
      { digest: shown.digest },
    )
      .then((response) => {
        if (!active()) return;
        const expiresAt = Math.min(
          Date.parse(response.cardTokenExpiresAt),
          Date.parse(shown.expiresAt),
        );
        const valid = response.cardToken && Number.isFinite(expiresAt) && expiresAt > Date.now();
        if (valid) setGrant({ key, token: response.cardToken, expiresAt });
        presented.current = valid && response.presented ? key : '';
      })
      .catch(() => {
        if (active()) presented.current = '';
      })
      .finally(() => {
        if (active()) requesting.current = '';
      });
  }, []);
  const key = current.current.key;
  useEffect(() => {
    reset();
    if (action) acknowledge(action);
    const retry = key
      ? setInterval(() => {
          if (current.current.action) acknowledge(current.current.action);
        }, 5000)
      : undefined;
    return () => {
      clearInterval(retry);
      reset();
    };
  }, [key, reset, acknowledge]);
  useEffect(() => {
    if (!grant || grant.key !== key) return;
    // Renew before expiry; timer never sends an approval. A failed renewal
    // leaves the card retryable via presentation/turn refresh, never authorized.
    const timer = setTimeout(
      () => {
        reset();
        if (current.current.action) acknowledge(current.current.action);
      },
      Math.max(1000, grant.expiresAt - Date.now() - 10_000),
    );
    return () => clearTimeout(timer);
  }, [grant, key, acknowledge, reset]);
  const token = () =>
    grant?.key === current.current.key && grant.expiresAt > Date.now() ? grant.token : null;
  return { acknowledge, reset, token, ready: Boolean(token()) };
}
